import React, { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { useAdminLayout } from '../AdminLayoutContext';
import { ADMIN_DOMAINS, applyAdminLayoutToDomains, buildAdminTarget, roleCanAccessTool, toolIsAvailable } from '../adminNavConfig';
import { PARTNER_ADMIN_DOMAINS } from '../partnerAdminNavConfig';
import AdminButton from './AdminButton';
import AdminSearchInput from './AdminSearchInput';
import './AdminSpotlight.css';

let spotlightOpen = false;
const spotlightListeners = new Set();

function emitSpotlightChange() {
  spotlightListeners.forEach((listener) => listener());
}

function setSpotlightState(nextValue) {
  const nextOpen = Boolean(nextValue);
  if (spotlightOpen === nextOpen) {
    return;
  }
  spotlightOpen = nextOpen;
  emitSpotlightChange();
}

function subscribeSpotlight(listener) {
  spotlightListeners.add(listener);
  return () => spotlightListeners.delete(listener);
}

export function useAdminSpotlight() {
  const open = useSyncExternalStore(subscribeSpotlight, () => spotlightOpen, () => false);
  return { open, setOpen: setSpotlightState };
}

function matchFuzzy(candidate = '', query = '') {
  const normalizedCandidate = candidate.toLowerCase();
  const normalizedQuery = query.trim().toLowerCase();

  if (!normalizedQuery) {
    return 0;
  }

  const parts = normalizedQuery.split(/\s+/).filter(Boolean);
  let totalScore = 0;

  for (const part of parts) {
    const directIndex = normalizedCandidate.indexOf(part);
    if (directIndex >= 0) {
      totalScore += directIndex;
      continue;
    }

    let cursor = -1;
    let gapScore = 0;
    for (const character of part) {
      const nextCursor = normalizedCandidate.indexOf(character, cursor + 1);
      if (nextCursor === -1) {
        return null;
      }
      if (cursor >= 0) {
        gapScore += nextCursor - cursor - 1;
      }
      cursor = nextCursor;
    }

    totalScore += gapScore + 24;
  }

  return totalScore;
}

function filterDomainsByRole(domains, role = 'admin', nodeRole = null) {
  return domains
    .map((domain) => {
      const tools = domain.tools.filter((tool) => (
        (!tool.requires || roleCanAccessTool(tool, role)) && toolIsAvailable(tool, nodeRole)
      ));
      if (!tools.length) {
        return null;
      }
      return {
        ...domain,
        tools,
        defaultTool: tools.some((tool) => tool.id === domain.defaultTool) ? domain.defaultTool : tools[0].id,
      };
    })
    .filter(Boolean);
}

function buildToolIndex(domains, basePath) {
  return domains.flatMap((domain) => domain.tools.map((tool) => ({
    id: `${domain.key}:${tool.id}`,
    domainKey: domain.key,
    domainLabel: domain.label,
    section: tool.section,
    label: tool.label,
    description: tool.description,
    target: buildAdminTarget(domain.key, tool.id, null, basePath),
    searchText: [tool.label, tool.description, tool.section, domain.label].join(' '),
  })));
}

function AdminSpotlight() {
  const { open, setOpen } = useAdminSpotlight();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { layout, nodeRole } = useAdminLayout();
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const activeRowRef = useRef(null);
  const role = user?.role || 'admin';
  const basePath = role === 'partner_admin' ? '/partner-admin' : '/admin';
  const navConfig = role === 'partner_admin' ? PARTNER_ADMIN_DOMAINS : ADMIN_DOMAINS;
  const toolIndex = useMemo(
    () => buildToolIndex(applyAdminLayoutToDomains(filterDomainsByRole(navConfig, role, nodeRole), layout), basePath),
    [basePath, layout, navConfig, nodeRole, role]
  );

  const results = useMemo(() => {
    return toolIndex
      .map((item) => {
        const score = matchFuzzy(item.searchText, query);
        return score === null ? null : { ...item, score };
      })
      .filter(Boolean)
      .sort((left, right) => left.score - right.score || left.label.localeCompare(right.label))
      .slice(0, 14);
  }, [query, toolIndex]);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search, location.hash, setOpen]);

  useEffect(() => {
    const handleOpen = () => setOpen(true);
    window.addEventListener('admin:open-spotlight', handleOpen);
    return () => window.removeEventListener('admin:open-spotlight', handleOpen);
  }, [setOpen]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    setQuery('');
    setActiveIndex(0);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.setTimeout(() => inputRef.current?.focus(), 0);

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (activeIndex > results.length - 1) {
      setActiveIndex(results.length ? 0 : -1);
    }
  }, [activeIndex, results.length]);

  useEffect(() => {
    if (activeRowRef.current) {
      activeRowRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveIndex((current) => {
          if (!results.length) {
            return -1;
          }
          return current >= results.length - 1 ? 0 : current + 1;
        });
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveIndex((current) => {
          if (!results.length) {
            return -1;
          }
          if (current <= 0) {
            return results.length - 1;
          }
          return current - 1;
        });
        return;
      }

      if (event.key === 'Enter' && results[activeIndex]) {
        event.preventDefault();
        navigate(results[activeIndex].target);
        setOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [activeIndex, navigate, open, results, setOpen]);

  if (!open || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div className="au-spotlight au-scope" role="presentation">
      <button type="button" className="au-spotlight__backdrop" aria-label="Close admin spotlight" onClick={() => setOpen(false)} />
      <section className="au-spotlight__panel" role="dialog" aria-modal="true" aria-label="Admin spotlight">
        <div className="au-spotlight__search">
          <AdminSearchInput
            ref={inputRef}
            placeholder="Jump to a tool…"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setActiveIndex(0);
            }}
            size="md"
            autoComplete="off"
            spellCheck="false"
          />
        </div>

        <div className="au-spotlight__results">
          {results.length ? (
            results.map((result, index) => (
              <button
                key={result.id}
                type="button"
                ref={index === activeIndex ? activeRowRef : null}
                className={`au-spotlight__result${index === activeIndex ? ' is-active' : ''}`}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => {
                  navigate(result.target);
                  setOpen(false);
                }}
              >
                <div className="au-spotlight__result-main">
                  <div className="au-spotlight__result-label">{result.label}</div>
                  <div className="au-spotlight__result-description">{result.description}</div>
                </div>
                <div className="au-spotlight__result-meta">
                  <span className="au-spotlight__domain-pill">{result.domainLabel}</span>
                  <span className="au-spotlight__section">{result.section}</span>
                </div>
              </button>
            ))
          ) : (
            <div className="au-spotlight__empty">
              <p className="au-spotlight__empty-title">No tools match &ldquo;{query}&rdquo;</p>
              <AdminButton variant="secondary" size="sm" onClick={() => setQuery('')}>
                Clear query
              </AdminButton>
            </div>
          )}
        </div>
      </section>
    </div>,
    document.body,
  );
}

export default AdminSpotlight;
